import { useEffect, useMemo, useState } from 'react';
import { Navbar } from '@/components/site/Navbar';
import { Footer } from '@/components/site/Footer';
import { sendContact } from '@/lib/api';
import { track } from '@/lib/analytics';
import { toast } from 'sonner';
import { setPageMeta } from '@/lib/seo';
import { useT, useLang } from '@/lib/i18n';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CheckCircle2, Mail } from 'lucide-react';

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const SUBJECT_KEYS = ['demo', 'partenariat', 'presse', 'autre'];

export default function Contact() {
  const t = useT();
  const { lang } = useLang();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('demo');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    setPageMeta({
      title: t('seo.contact_title'),
      description: t('seo.contact_description'),
      path: '/contact',
    });
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [t, lang]);

  const subjects = useMemo(() => SUBJECT_KEYS.map((k) => ({ value: k, label: t(`contact.subjects.${k}`) })), [t]);

  const validate = () => {
    const e = {};
    const n = name.trim();
    if (n.length < 2) e.name = t('contact.validation.name_required');
    if (n.length > 80) e.name = t('contact.validation.name_too_long');
    const em = email.trim();
    if (!em) e.email = t('contact.validation.email_required');
    else if (em.length > 254 || !EMAIL_RE.test(em)) e.email = t('contact.validation.email_invalid');
    if (!SUBJECT_KEYS.includes(subject)) e.subject = t('contact.validation.subject_required');
    const m = message.trim();
    if (m.length < 10) e.message = t('contact.validation.message_min');
    else if (m.length > 2000) e.message = t('contact.validation.message_max');
    return e;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (loading) return;
    const eMap = validate();
    setErrors(eMap);
    if (Object.keys(eMap).length > 0) {
      toast.error(t('contact.error_generic_toast'), { duration: 4000 });
      return;
    }
    setLoading(true);
    const res = await sendContact({
      name: name.trim(),
      email: email.trim(),
      subject,
      message: message.trim(),
    });
    setLoading(false);
    if (res.ok) {
      setSent(true);
      setName(''); setEmail(''); setSubject('demo'); setMessage(''); setErrors({});
      track('contact_submitted', { lang });
      toast.success(t('contact.success_toast'), { duration: 6000 });
    } else {
      toast.error(t(`errors.${res.code || 'contact_generic'}`), { duration: 5000 });
    }
  };

  const remaining = 2000 - message.length;

  return (
    <div data-testid="page-contact">
      <Navbar />
      <main>
        <section className="relative overflow-hidden pt-[128px] md:pt-[168px] pb-20">
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
            <div
              className="absolute right-[-8%] -top-24 h-[420px] w-[420px] rounded-full blur-3xl"
              style={{ background: 'radial-gradient(closest-side, rgba(249,115,22,0.24), transparent 70%)' }}
            />
            <div
              className="absolute left-[-8%] bottom-[-10%] h-[360px] w-[360px] rounded-full blur-3xl"
              style={{ background: 'radial-gradient(closest-side, rgba(139,92,246,0.18), transparent 70%)' }}
            />
          </div>

          <div className="om-container">
            <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <span className="om-eyebrow">{t('contact.eyebrow')}</span>
                <h1 className="om-h1 mt-6 text-[42px] sm:text-[52px] lg:text-[60px] text-[var(--om-ink)]">
                  {t('contact.title_l1')}<br />{t('contact.title_l2')}
                </h1>
                <p className="mt-6 max-w-lg text-base md:text-lg leading-relaxed text-[var(--om-muted)]">
                  {t('contact.subtitle')}
                </p>

                <div className="mt-10 flex items-center gap-3 rounded-2xl border border-[var(--om-border)] bg-white p-5">
                  <div className="grid h-10 w-10 place-items-center rounded-full bg-[var(--om-ink)] text-[var(--om-btn-primary-fg)]">
                    <Mail size={18} strokeWidth={2.4} />
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                      {t('contact.email_label_short')}
                    </div>
                    <a href="mailto:hello@openmeta.tech" className="text-sm font-semibold text-[var(--om-ink)] hover:underline">
                      hello@openmeta.tech
                    </a>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7">
                <form
                  onSubmit={handleSubmit}
                  noValidate
                  data-testid="contact-form"
                  className="relative rounded-[28px] border border-[var(--om-border)] bg-white p-8 shadow-[0_30px_60px_-40px_rgba(10,14,26,0.2)] md:p-10"
                >
                  {sent && (
                    <div
                      data-testid="contact-success"
                      className="mb-6 flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800"
                    >
                      <CheckCircle2 size={18} strokeWidth={2.2} className="mt-0.5 shrink-0" />
                      <div>
                        <div className="font-semibold">{t('contact.success_title')}</div>
                        <div className="text-emerald-700">{t('contact.success_subtitle')}</div>
                      </div>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                        {t('contact.name_label')}
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => { setName(e.target.value); if (errors.name) setErrors({ ...errors, name: '' }); }}
                        placeholder={t('contact.name_placeholder')}
                        data-testid="contact-name-input"
                        aria-invalid={errors.name ? 'true' : 'false'}
                        className={`om-input ${errors.name ? 'border-red-400' : ''}`}
                      />
                      {errors.name && <div className="mt-1.5 pl-5 text-xs text-red-600" data-testid="contact-error-name">{errors.name}</div>}
                    </div>
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                        {t('contact.email_label')}
                      </label>
                      <input
                        type="text"
                        inputMode="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors({ ...errors, email: '' }); }}
                        placeholder={t('common.email_placeholder')}
                        data-testid="contact-email-input"
                        aria-invalid={errors.email ? 'true' : 'false'}
                        className={`om-input ${errors.email ? 'border-red-400' : ''}`}
                      />
                      {errors.email && <div className="mt-1.5 pl-5 text-xs text-red-600" data-testid="contact-error-email">{errors.email}</div>}
                    </div>
                  </div>

                  <div className="mt-5">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                      {t('contact.subject_label')}
                    </label>
                    <Select value={subject} onValueChange={setSubject} data-testid="contact-subject-select">
                      <SelectTrigger className="om-input flex h-12 items-center justify-between" data-testid="contact-subject-trigger">
                        <SelectValue placeholder={t('contact.subject_placeholder')} />
                      </SelectTrigger>
                      <SelectContent className="rounded-2xl">
                        {subjects.map((s) => (
                          <SelectItem key={s.value} value={s.value} data-testid={`contact-subject-${s.value}`}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="mt-5">
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-[var(--om-muted-2)]">
                      {t('contact.message_label')}
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => { setMessage(e.target.value); if (errors.message) setErrors({ ...errors, message: '' }); }}
                      placeholder={t('contact.message_placeholder')}
                      rows={6}
                      data-testid="contact-message-input"
                      aria-invalid={errors.message ? 'true' : 'false'}
                      className={`w-full rounded-2xl border bg-white px-5 py-4 text-sm text-[var(--om-ink)] placeholder:text-[var(--om-muted-2)] outline-none transition-all duration-200 focus:border-[var(--om-ink)] focus:ring-2 focus:ring-black/10 ${
                        errors.message ? 'border-red-400' : 'border-[var(--om-border)]'
                      }`}
                    />
                    <div className="mt-2 flex items-center justify-between">
                      {errors.message ? (
                        <span className="text-xs text-red-600" data-testid="contact-error-message">{errors.message}</span>
                      ) : (
                        <span className="text-xs text-[var(--om-muted-2)]">{t('contact.message_hint')}</span>
                      )}
                      <span className={`text-xs tabular-nums ${remaining < 0 ? 'text-red-600' : 'text-[var(--om-muted-2)]'}`}>
                        {remaining}
                      </span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                    className="om-btn-primary mt-8 w-full sm:w-auto"
                  >
                    {loading ? t('contact.submitting') : t('contact.submit')}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
